import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const BrandMetadataModal = ({ show, onHide, brandId, onUpdateSuccess }) => {
  const [brandData, setBrandData] = useState(null);
  const [formData, setFormData] = useState({
    contact_email: '',
    description: '',
    contact_number: '',
    address: '',
    configuration: {
      theme_color: '',
      notifications_enabled: true,
      max_users: 100
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { token } = useSelector((state) => state.user);

  // Fetch brand details when modal opens
  const fetchBrandDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${brandId}/details/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch brand details');
      }

      const data = await response.json();
      setBrandData(data);
      setFormData({
        contact_email: data.contact_email || '',
        description: data.description || '',
        contact_number: data.contact_number || '',
        address: data.address || '',
        configuration: {
          theme_color: data.configuration?.theme_color || '#FF5733',
          notifications_enabled: data.configuration?.notifications_enabled ?? true,
          max_users: data.configuration?.max_users || 100
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, token]);

  useEffect(() => {
    if (show && brandId) {
      fetchBrandDetails();
    }
  }, [show, brandId, fetchBrandDetails]);

  const handleInputChange = (field, value) => {
    if (field.startsWith('configuration.')) {
      const configField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${brandId}/update/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to update brand metadata');
      }

      const updatedData = await response.json();
      setSuccess('Brand metadata updated successfully!');
      setBrandData(updatedData);
      
      // Call the success callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess(updatedData);
      }

      // Auto-close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    onHide();
  };

  return (
    <>
      {show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }} onClick={handleClose}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 0,
            minWidth: 600,
            maxWidth: 800,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 32px rgba(60,60,90,0.18)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={e => e.stopPropagation()}>
            
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f9fafb',
              borderRadius: '16px 16px 0 0'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 600,
                color: '#004443'
              }}>
                {brandData ? `Manage ${brandData.name} Metadata` : 'Manage Brand Metadata'}
              </h2>
              <button onClick={handleClose} style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}>×</button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              {loading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#666'
                }}>
                  <div style={{
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    border: '2px solid #f0f0f0',
                    borderTop: '2px solid #00bfae',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '10px'
                  }}></div>
                  <p>Loading brand details...</p>
                </div>
              ) : (
                <>
                  {error && (
                    <div style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      {error}
                    </div>
                  )}
                  
                  {success && (
                    <div style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      color: '#166534',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      {success}
                    </div>
                  )}

                  {brandData && (
                    <form onSubmit={handleSubmit} style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      {/* Brand Info Section */}
                      <div style={{
                        background: '#f8fafc',
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h4 style={{
                          margin: '0 0 16px 0',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#004443',
                          borderBottom: '2px solid #00bfae',
                          paddingBottom: '8px',
                          display: 'inline-block'
                        }}>Brand Information</h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Brand Name
                            </label>
                            <input
                              type="text"
                              value={brandData.name}
                              disabled
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                background: '#f1f5f9',
                                color: '#64748b',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                boxSizing: 'border-box'
                              }}
                            />
                            <small style={{
                              color: '#64748b',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              marginTop: '4px',
                              display: 'block'
                            }}>Cannot be modified</small>
                          </div>
                          
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Domain
                            </label>
                            <input
                              type="text"
                              value={brandData.domain}
                              disabled
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                background: '#f1f5f9',
                                color: '#64748b',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                boxSizing: 'border-box'
                              }}
                            />
                            <small style={{
                              color: '#64748b',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              marginTop: '4px',
                              display: 'block'
                            }}>Cannot be modified</small>
                          </div>
                        </div>
                      </div>

                      {/* Contact Information Section */}
                      <div style={{
                        background: '#ffffff',
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{
                          margin: '0 0 16px 0',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#004443',
                          borderBottom: '2px solid #00bfae',
                          paddingBottom: '8px',
                          display: 'inline-block'
                        }}>Contact Information</h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Contact Email *
                            </label>
                            <input
                              type="email"
                              value={formData.contact_email}
                              onChange={(e) => handleInputChange('contact_email', e.target.value)}
                              required
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #cbd5e1',
                                borderRadius: '8px',
                                background: '#ffffff',
                                color: '#1e293b',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                boxSizing: 'border-box',
                                outline: 'none'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#00bfae'}
                              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                          </div>
                          
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Contact Number
                            </label>
                            <input
                              type="tel"
                              value={formData.contact_number}
                              onChange={(e) => handleInputChange('contact_number', e.target.value)}
                              placeholder="+1 (555) 123-4567"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #cbd5e1',
                                borderRadius: '8px',
                                background: '#ffffff',
                                color: '#1e293b',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                boxSizing: 'border-box',
                                outline: 'none'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#00bfae'}
                              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                          </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: 600,
                            color: '#004443',
                            fontSize: '13px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Description
                          </label>
                          <textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Tell us about your brand, mission, and values..."
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '2px solid #cbd5e1',
                              borderRadius: '8px',
                              background: '#ffffff',
                              color: '#1e293b',
                              fontSize: '14px',
                              fontWeight: 500,
                              transition: 'all 0.2s',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.5',
                              boxSizing: 'border-box',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#00bfae'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: 600,
                            color: '#004443',
                            fontSize: '13px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Address
                          </label>
                          <textarea
                            rows={2}
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            placeholder="Full business address including city, state, and postal code..."
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '2px solid #cbd5e1',
                              borderRadius: '8px',
                              background: '#ffffff',
                              color: '#1e293b',
                              fontSize: '14px',
                              fontWeight: 500,
                              transition: 'all 0.2s',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.5',
                              boxSizing: 'border-box',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#00bfae'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                          />
                        </div>
                      </div>

                      {/* Configuration Section */}
                      <div style={{ 
                        background: '#f0f9ff',
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        border: '1px solid #bae6fd',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{
                          margin: '0 0 16px 0',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#004443',
                          borderBottom: '2px solid #00bfae',
                          paddingBottom: '8px',
                          display: 'inline-block'
                        }}>
                          Configuration Settings
                        </h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '24px', alignItems: 'start' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              textAlign: 'center'
                            }}>
                              Theme Color
                            </label>
                            <div style={{ 
                              position: 'relative',
                              padding: '4px',
                              background: '#ffffff',
                              borderRadius: '12px',
                              border: '2px solid #cbd5e1',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <input
                                type="color"
                                value={formData.configuration.theme_color}
                                onChange={(e) => handleInputChange('configuration.theme_color', e.target.value)}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                            <small style={{
                              color: '#64748b',
                              fontSize: '11px',
                              textAlign: 'center',
                              marginTop: '4px'
                            }}>{formData.configuration.theme_color}</small>
                          </div>
                          
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 600,
                              color: '#004443',
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Maximum Users
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              value={formData.configuration.max_users}
                              onChange={(e) => handleInputChange('configuration.max_users', parseInt(e.target.value))}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #cbd5e1',
                                borderRadius: '8px',
                                background: '#ffffff',
                                color: '#1e293b',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                boxSizing: 'border-box',
                                outline: 'none'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#00bfae'}
                              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                            <small style={{
                              color: '#64748b',
                              fontSize: '12px',
                              display: 'block',
                              marginTop: '4px'
                            }}>Number of users allowed</small>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '8px' }}>
                            <label style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '12px',
                              background: formData.configuration.notifications_enabled ? '#dcfce7' : '#fef2f2',
                              border: `2px solid ${formData.configuration.notifications_enabled ? '#bbf7d0' : '#fecaca'}`,
                              transition: 'all 0.2s'
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                border: '2px solid',
                                borderColor: formData.configuration.notifications_enabled ? '#16a34a' : '#dc2626',
                                background: formData.configuration.notifications_enabled ? '#16a34a' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '8px',
                                transition: 'all 0.2s'
                              }}>
                                {formData.configuration.notifications_enabled && (
                                  <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.configuration.notifications_enabled}
                                onChange={(e) => handleInputChange('configuration.notifications_enabled', e.target.checked)}
                                style={{ display: 'none' }}
                              />
                              <span style={{
                                fontWeight: 600,
                                color: '#004443',
                                fontSize: '12px',
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Notifications
                              </span>
                              <span style={{
                                color: formData.configuration.notifications_enabled ? '#16a34a' : '#dc2626',
                                fontSize: '11px',
                                fontWeight: 500
                              }}>
                                {formData.configuration.notifications_enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Subscription Details */}
                      {brandData.subscription && (
                        <div style={{ 
                          background: '#fefce8',
                          padding: '20px',
                          borderRadius: '12px',
                          marginBottom: '20px',
                          border: '1px solid #fde047',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <h4 style={{
                            margin: '0 0 16px 0',
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#004443',
                            borderBottom: '2px solid #eab308',
                            paddingBottom: '8px',
                            display: 'inline-block'
                          }}>
                            Subscription Details
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: 600,
                                color: '#004443',
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Current Plan
                              </label>
                              <div style={{
                                padding: '12px 16px',
                                background: '#ffffff',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                color: '#1e293b',
                                fontSize: '14px',
                                fontWeight: 600,
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                              }}>
                                {brandData.subscription.plan_name}
                              </div>
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: 600,
                                color: '#004443',
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Start Date
                              </label>
                              <div style={{
                                padding: '12px 16px',
                                background: '#ffffff',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                color: '#64748b',
                                fontSize: '14px',
                                fontWeight: 500
                              }}>
                                {new Date(brandData.subscription.start_date).toLocaleDateString()}
                              </div>
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: 600,
                                color: '#004443',
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Auto Renewal
                              </label>
                              <div style={{
                                padding: '12px 16px',
                                background: brandData.subscription.auto_renew ? '#dcfce7' : '#fef2f2',
                                border: `2px solid ${brandData.subscription.auto_renew ? '#bbf7d0' : '#fecaca'}`,
                                borderRadius: '8px',
                                color: brandData.subscription.auto_renew ? '#16a34a' : '#dc2626',
                                fontSize: '14px',
                                fontWeight: 600,
                                textAlign: 'center',
                                textTransform: 'uppercase'
                              }}>
                                {brandData.subscription.auto_renew ? '✓ Enabled' : '✗ Disabled'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </form>
                  )}
                </>
              )}
            </div>
            
            {/* Footer */}
            {brandData && (
              <div style={{
                padding: '20px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '16px',
                background: '#f8fafc',
                borderRadius: '0 0 16px 16px'
              }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={saving}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #cbd5e1',
                    borderRadius: '8px',
                    background: '#ffffff',
                    color: '#64748b',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => !saving && (e.target.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => !saving && (e.target.style.background = '#ffffff')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || saving}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    background: saving ? '#64748b' : 'linear-gradient(135deg, #00bfae 0%, #004443 100%)',
                    color: '#ffffff',
                    cursor: saving || loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    boxShadow: saving ? 'none' : '0 4px 12px rgba(0, 191, 174, 0.3)',
                    opacity: (saving || loading) ? 0.7 : 1,
                    transform: (saving || loading) ? 'scale(0.98)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving && !loading) {
                      e.target.style.transform = 'scale(1.02)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0, 191, 174, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving && !loading) {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 191, 174, 0.3)';
                    }
                  }}
                >
                  {saving && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  )}
                  {saving ? 'Saving Changes...' : '💾 Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Add CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default BrandMetadataModal;
